/**
 * Вудка: casting, waiting, and the moment it bites.
 *
 * Fishing is the first thing in this game that is a *decision spread over
 * time*. Everything else resolves the instant you press the button: a block
 * breaks, an arrow flies, an animal is hit. Here you throw the line, you wait
 * without knowing how long, and when it goes you have about a second and a
 * half to answer - and pulling early costs you the cast.
 *
 * That is the whole point of putting it in a file of its own rather than in
 * three flags in the game loop. It is a state machine with two clocks, it is
 * exactly the kind of thing that goes subtly wrong, and here it can be run
 * ten thousand times in a test instead of by standing on a bank.
 *
 * Pure: two numbers and an enum. No world, no water, no engine - whether
 * there is anywhere to cast *to* is the caller's business.
 */

/** Shortest and longest wait before something takes an interest, in seconds. */
export const WAIT_MIN = 4;
export const WAIT_MAX = 18;
/**
 * How long the fish is on, once it is on.
 *
 * A second and a half: long enough to answer a sound, short enough that you
 * have to be paying attention rather than holding the button down.
 */
export const BITE_SECONDS = 1.5;

export type LineState =
  /** In hand, nothing in the water. */
  | "idle"
  /** Cast, and waiting. */
  | "waiting"
  /** Something is taking it. Pull now. */
  | "biting";

/** What a tick of the line produced, for the caller to make a noise about. */
export type AnglingEvent = "none" | "bite" | "lost";

/** What came of pulling. */
export type Catch =
  /** Nothing was in the water to begin with. */
  | "not-cast"
  /** Pulled while it was still waiting: the cast is spent. */
  | "too-early"
  /** A карась. */
  | "caught";

export class FishingLine {
  private state: LineState = "idle";
  /** Seconds until the next thing happens, whatever that is. */
  private timer = 0;

  get status(): LineState {
    return this.state;
  }

  /** Seconds left of whatever it is doing, for a gauge on the HUD. */
  get left(): number {
    return this.timer;
  }

  /**
   * Throw the line in.
   *
   * `random` is injectable so a test can pin the wait instead of sitting
   * through it. Casting again while already cast does nothing: the button is
   * the same one that pulls, and the caller decides which it means.
   */
  cast(random: () => number = Math.random): void {
    if (this.state !== "idle") return;
    this.state = "waiting";
    this.timer = WAIT_MIN + random() * (WAIT_MAX - WAIT_MIN);
  }

  /** Reel in with nothing decided - walking away, or dying. */
  reset(): void {
    this.state = "idle";
    this.timer = 0;
  }

  /**
   * Run the clocks.
   *
   * Reports the two moments worth a sound: the bite, and losing it by not
   * answering. A miss puts the line back in the water rather than into the
   * hand, because a fish getting away should cost the wait and not the cast.
   */
  step(dt: number, random: () => number = Math.random): AnglingEvent {
    if (this.state === "idle") return "none";

    this.timer -= dt;
    if (this.timer > 0) return "none";

    if (this.state === "waiting") {
      this.state = "biting";
      this.timer = BITE_SECONDS;
      return "bite";
    }

    // It was biting and nobody answered.
    this.state = "waiting";
    this.timer = WAIT_MIN + random() * (WAIT_MAX - WAIT_MIN);
    return "lost";
  }

  /** Strike. Leaves the line out of the water either way. */
  pull(): Catch {
    if (this.state === "idle") return "not-cast";
    const biting = this.state === "biting";
    this.reset();
    return biting ? "caught" : "too-early";
  }
}
